<template>
  <b-modal
    id="modal-download-photo"
    modal-class="modal-download-photo"
    :title="$t('fleetTask.downloadPhoto.title')"
    size="lg"
    centered
    scrollable
    @shown="handleModalShown"
    @hidden="handleModalHidden"
  >
    <b-table
      :outlined="true"
      :items="listPhoto"
      :fields="fields"
      :busy="isLoading"
      responsive
      show-empty
    >
      <template #table-busy>
        <div class="text-center my-3">
          <b-spinner class="align-middle mr-2" />
          <strong>{{ $t('global.text.loading') }}</strong>
        </div>
      </template>

      <template #cell(status)="slotProps">
        <McBadge :text="slotProps.item.status" :variant="slotProps.item.color" type="status" />
      </template>

      <template #cell(action)="slotProps">
        <McButton
          :text="slotProps.item.action"
          :icon="slotProps.item.actionIcon"
          :disabled="isActionDisabled(slotProps.item)"
          size="sm"
          @click="handleAction(slotProps.item)"
        >
          <template #after-slot>
            <span v-if="slotProps.item.statusKey === DOWNLOAD_ATTACHMENT_STATUS.IN_PROGRESS">
              <b-spinner small />
            </span>
          </template>
        </McButton>
      </template>
    </b-table>
    <div class="text-start my-2">{{ $t('global.show') }} {{ attachmentListCount }} data</div>
    <template #modal-footer="slotProps">
      <McButton :text="$t('global.action.close')" @click="slotProps.cancel()" />
    </template>
  </b-modal>
</template>
<script>
import McBadge from '@/components/McBadge'
import McButton from '@/components/McButton'
import {
  requestDownloadAttachment,
  getListDownloadAttachment,
} from '@/services/rest-api/lib/fleet-task/download-attachment'
import { DOWNLOAD_ATTACHMENT_STATUS, DISABLED_ACTION_KEYS } from './lib/constants'
import {
  normalizeDownloadAttachmentList,
  getDownloadAttachmentActivityType,
  mapToTableItem,
} from './lib/utils'
import { AttachmentFields } from './lib/fields'

const POLLING_INTERVAL_MS = 5000
const REQUEST_REFRESH_DELAY_MS = 2000

export default {
  components: {
    McBadge,
    McButton,
  },
  props: {
    fleetTaskUuid: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      DOWNLOAD_ATTACHMENT_STATUS,
      attachmentList: [],
      isLoading: false,
      pollingTimer: null,
    }
  },
  computed: {
    listPhoto() {
      return this.attachmentList.map((item, index) =>
        mapToTableItem(item, (key) => this.$t(key), index)
      )
    },
    fields() {
      return AttachmentFields(this)
    },
    hasProcessingItem() {
      return this.attachmentList.some(
        (item) => item.status === DOWNLOAD_ATTACHMENT_STATUS.IN_PROGRESS
      )
    },
    attachmentListCount() {
      return this.attachmentList.length
    },
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    async handleModalShown() {
      this.attachmentList = []
      await this.fetchAttachmentList()
    },
    handleModalHidden() {
      this.attachmentList = []
      this.stopPolling()
    },
    async fetchAttachmentList(silent = false) {
      if (!silent) this.isLoading = true
      const previousStatusMap = this.buildStatusMap(this.attachmentList)
      try {
        const data = await getListDownloadAttachment(this.fleetTaskUuid)
        const normalized = normalizeDownloadAttachmentList(data)
        this.notifyStatusChanges(previousStatusMap, normalized)
        this.attachmentList = normalized
      } catch {
        if (!silent) {
          this.attachmentList = []
        }
        this.$mcToast.error('Gagal memuat data, silahkan coba lagi')
      } finally {
        if (!silent) this.isLoading = false
        this.proccessingItem()
      }
    },
    buildStatusMap(list) {
      return list.reduce((map, item) => {
        const activityType =
          item.activityType || getDownloadAttachmentActivityType(item.fileName || item.name)
        map[activityType] = item.status
        return map
      }, {})
    },
    notifyStatusChanges(previousStatusMap, newList) {
      newList.forEach((item) => {
        const activityType =
          item.activityType || getDownloadAttachmentActivityType(item.fileName || item.name)
        const previousStatus = previousStatusMap[activityType]

        if (previousStatus === undefined || previousStatus === item.status) return

        if (item.status === DOWNLOAD_ATTACHMENT_STATUS.SUCCESS) {
          this.$mcToast.success(`Berhasil memproses unduhan, silahkan download file anda`)
        } else if (item.status === DOWNLOAD_ATTACHMENT_STATUS.FAILED) {
          this.$mcToast.error('Gagal memproses file, silahkan coba lagi')
        }
      })
    },
    proccessingItem() {
      this.stopPolling()
      if (!this.hasProcessingItem) return

      this.pollingTimer = setTimeout(async () => {
        await this.fetchAttachmentList(true)
      }, POLLING_INTERVAL_MS)
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearTimeout(this.pollingTimer)
        this.pollingTimer = null
      }
    },
    isActionDisabled(item) {
      return DISABLED_ACTION_KEYS.includes(item.statusKey)
    },
    async handleAction(item) {
      if (item.statusKey === DOWNLOAD_ATTACHMENT_STATUS.SUCCESS) {
        this.download(item)
        return
      }
      await this.requestAttachment(item)
    },
    async requestAttachment(item) {
      const activityType =
        item.activityType || getDownloadAttachmentActivityType(item.fileName || item.name)
      if (!activityType) return

      this.setAttachmentStatus(activityType, DOWNLOAD_ATTACHMENT_STATUS.IN_PROGRESS)
      try {
        await requestDownloadAttachment(this.fleetTaskUuid, activityType)
        await this.wait(REQUEST_REFRESH_DELAY_MS)
        await this.fetchAttachmentList(true)
      } catch {
        this.setAttachmentStatus(activityType, DOWNLOAD_ATTACHMENT_STATUS.FAILED)
        this.$mcToast.error('Gagal memproses file, silahkan coba lagi')
      }
    },
    setAttachmentStatus(activityType, status) {
      this.attachmentList = this.attachmentList.map((attachment) => {
        const attachmentActivityType =
          attachment.activityType ||
          getDownloadAttachmentActivityType(attachment.fileName || attachment.name)

        if (attachmentActivityType !== activityType) return attachment

        return {
          ...attachment,
          status,
        }
      })
    },
    wait(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms))
    },
    download(item) {
      if (item.url) {
        window.open(item.url, '_blank')
        return
      }
    },
  },
}
</script>
